
import { CircleAlert as AlertCircle, Info, Lightbulb, CircleCheck as CheckCircle } from 'lucide-react';
import { ReactNode } from 'react';

interface ContentBoxProps {
  children: ReactNode;
  title?: string;
}

export function InfoBox({ children, title = 'Informação' }: ContentBoxProps) {
  return (
    <div className="my-6 rounded-xl border border-blue-200 bg-blue-50 p-5 shadow-sm">
      <div className="flex gap-3">
        <div className="flex-shrink-0">
          <Info className="h-5 w-5 text-blue-600" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-blue-900 mb-2">{title}</h4>
          <div className="text-sm text-blue-800 leading-relaxed">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function WarningBox({ children, title = 'Atenção' }: ContentBoxProps) {
  return (
    <div className="my-6 rounded-xl border border-orange-200 bg-orange-50 p-5 shadow-sm">
      <div className="flex gap-3">
        <div className="flex-shrink-0">
          <AlertCircle className="h-5 w-5 text-orange-600" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-orange-900 mb-2">{title}</h4>
          <div className="text-sm text-orange-800 leading-relaxed">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function TipBox({ children, title = 'Dica' }: ContentBoxProps) {
  return (
    <div className="my-6 rounded-xl border border-green-200 bg-green-50 p-5 shadow-sm">
      <div className="flex gap-3">
        <div className="flex-shrink-0">
          <Lightbulb className="h-5 w-5 text-green-600" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-green-900 mb-2">{title}</h4>
          <div className="text-sm text-green-800 leading-relaxed">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function SuccessBox({ children, title = 'Sucesso' }: ContentBoxProps) {
  return (
    <div className="my-6 rounded-xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm">
      <div className="flex gap-3">
        <div className="flex-shrink-0">
          <CheckCircle className="h-5 w-5 text-emerald-600" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-emerald-900 mb-2">{title}</h4>
          <div className="text-sm text-emerald-800 leading-relaxed">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function CodeBlock({ children, language = 'text' }: { children: string; language?: string }) {
  return (
    <div className="my-6 rounded-xl overflow-hidden border border-gray-200 shadow-sm">
      <div className="bg-gray-800 px-4 py-2 flex items-center justify-between">
        <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">{language}</span>
      </div>
      <pre className="bg-gray-900 p-4 overflow-x-auto">
        <code className="text-sm text-gray-100 font-mono">{children}</code>
      </pre>
    </div>
  );
}

export function HighlightText({ children }: { children: ReactNode }) {
  return (
    <span className="bg-yellow-100 text-yellow-900 px-1.5 py-0.5 rounded font-medium">
      {children}
    </span>
  );
}

export function ArticleImage({ src, alt, caption }: { src: string; alt: string; caption?: string }) {
  return (
    <figure className="my-8">
      <div className="rounded-xl overflow-hidden border border-gray-200 shadow-md">
        <img
          src={src}
          alt={alt}
          className="w-full h-auto"
        />
      </div>
      {caption && (
        <figcaption className="mt-3 text-sm text-gray-600 text-center italic">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

export function StepList({ children }: { children: ReactNode }) {
  return (
    <ol className="my-6 space-y-4">
      {children}
    </ol>
  );
}

export function Step({ number, children }: { number: number; children: ReactNode }) {
  return (
    <li className="flex gap-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold text-sm">
        {number}
      </div>
      <div className="flex-1 pt-1 text-gray-700 leading-relaxed">
        {children}
      </div>
    </li>
  );
}
